import React, { useState, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { UserIcon, FilterIcon, SearchIcon, LayoutGridIcon, LayoutListIcon } from 'lucide-react';
import BackButton from '../../components/UI/BackButton';
import { useDarkMode } from '../../context/DarkModeContext';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { ProfileCard } from '../../components/Directory/ProfileCard';
import { mapAuthorToProfileCard } from '../../utils/profileCardMappers';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../../components/UI/FilterSidebar';
// Mock data for authors
const authors = [{
  id: '1',
  name: 'Elena Richards',
  slug: 'elena-richards',
  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Bestselling author of the Echo Series and multiple stand-alone thrillers.',
  genres: ['Mystery', 'Thriller', 'Suspense'],
  rating: 4.8,
  bookCount: 12,
  featured: true,
  location: 'New York, NY',
  awards: ['Mystery Writers of America Award', 'Thriller Award Finalist']
}, {
  id: '2',
  name: 'Marcus Chen',
  slug: 'marcus-chen',
  avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Fantasy writer known for rich world-building and diverse characters.',
  genres: ['Fantasy', 'Young Adult', 'Urban Fantasy'],
  rating: 4.7,
  bookCount: 8,
  featured: true,
  location: 'Seattle, WA',
  awards: ['Hugo Award Nominee', 'Locus Award Winner']
}, {
  id: '3',
  name: 'Sarah Johnson',
  slug: 'sarah-johnson',
  avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Horror and supernatural fiction author with a flair for the psychological.',
  genres: ['Horror', 'Supernatural', 'Gothic'],
  rating: 4.9,
  bookCount: 15,
  featured: false,
  location: 'Portland, OR',
  awards: ['Bram Stoker Award Winner', 'Shirley Jackson Award']
}, {
  id: '4',
  name: 'David Williams',
  slug: 'david-williams',
  avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Historical fiction author specializing in adventure and exploration narratives.',
  genres: ['Historical Fiction', 'Adventure', 'Action'],
  rating: 4.6,
  bookCount: 10,
  featured: false,
  location: 'Boston, MA',
  awards: ['Historical Novel Society Award']
}, {
  id: '5',
  name: 'Amara Knight',
  slug: 'amara-knight',
  avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Science fiction author exploring the intersection of technology and humanity.',
  genres: ['Science Fiction', 'Dystopian', 'Romance'],
  rating: 4.8,
  bookCount: 7,
  featured: true,
  location: 'Austin, TX',
  awards: ['Nebula Award Winner', 'Philip K. Dick Award Nominee']
}];
const AuthorDirectory = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('name-asc');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  // Sidebar states
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const allGenres = Array.from(new Set(authors.flatMap(author => author.genres))).sort();
  const filteredAuthors = authors.filter(author => {
    const matchesSearch = searchTerm === '' || author.name.toLowerCase().includes(searchTerm.toLowerCase()) || author.bio.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGenres = selectedGenres.length === 0 || selectedGenres.some(genre => author.genres.includes(genre));
    return matchesSearch && matchesGenres;
  });
  // Sort logic
  filteredAuthors.sort((a, b) => {
    switch (sortBy) {
      case 'name-asc':
        return a.name.localeCompare(b.name);
      case 'name-desc':
        return b.name.localeCompare(a.name);
      case 'books-desc':
        return b.bookCount - a.bookCount;
      case 'rating-desc':
        return b.rating - a.rating;
      default:
        return 0;
    }
  });
  const toggleGenre = (genre: string) => {
    setSelectedGenres(prev => prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre]);
  };
  const resetFilters = () => {
    setSelectedGenres([]);
    setSortBy('name-asc');
    setSearchTerm('');
  };
  const toggleMobileFilters = () => {
    setMobileFiltersOpen(!mobileFiltersOpen);
    document.body.style.overflow = !mobileFiltersOpen ? 'hidden' : 'auto';
  };
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'name-asc',
      label: 'A-Z'
    }, {
      value: 'name-desc',
      label: 'Z-A'
    }, {
      value: 'books-desc',
      label: 'Most Books'
    }, {
      value: 'rating-desc',
      label: 'Highest Rated'
    }],
    value: sortBy,
    onChange: setSortBy
  }, {
    id: 'genres',
    title: 'Genres',
    type: 'checkbox',
    options: allGenres.map(genre => ({
      value: genre,
      label: genre
    })),
    value: selectedGenres,
    onChange: toggleGenre
  }];
  return <div className={`min-h-screen ${dk.page}`} data-id="element-18260" data-mp="1ykrcdiz74zfc">
      {/* Page Header */}
      <div className={`pt-24 md:pt-8 pb-6 px-4 sm:px-6 lg:px-8`} data-id="element-18261" data-mp="1ym2e7d92zx3t">
        <div className="max-w-7xl mx-auto" data-id="element-18262" data-mp="510jzlymhdb0">
          <div className="mb-4" data-id="element-18263" data-mp="53v57fpgn25h">
            <BackButton data-id="element-18264" data-mp="2flbqz0r1oo" />
          </div>
          <h1 className={`text-3xl font-bold mb-2 ${dk.textHeading}`} data-id="element-18265" data-mp="3qy2gafzjrx">
            Author Directory
          </h1>
          <p className={`text-lg max-w-2xl ${dk.textSecondary}`} data-id="element-18266" data-mp="3rfjwte7y8kfd">
            Connect with talented authors for your publishing projects,
            collaborations, and events.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" data-id="element-18267" data-mp="3dhvdwbxfzncy">
        {/* Mobile Filter Button */}
        <div className="lg:hidden mb-6" data-id="element-18268" data-mp="38iipvnuu4a8s">
          <button onClick={toggleMobileFilters} className={`w-full flex items-center justify-center px-4 py-3 border rounded-md transition-colors ${dk.card}`} data-id="element-18269" data-mp="38jk4g2b27ev9">
            <FilterIcon className="h-5 w-5 mr-2" data-id="element-18270" data-mp="16q2yd32kum2m" />
            <span data-id="element-18271" data-mp="11sb5a5q74twu">Filters & Sort</span>
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8" data-id="element-18272" data-mp="11t42og39m72b">
          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={toggleSidebar} data-id="element-18273" data-mp="19bk3r3x5z874">
            <FilterSidebar title="Filters" sections={filterSections} onReset={resetFilters} data-id="element-18274" data-mp="1o0qdhltmogt9" />
          </FilterSidebarWrapper>

          {/* Main Content */}
          <main className="flex-1 min-w-0" data-id="element-18275" data-mp="vbsuthli8hzz">
            {/* Search and View Toggle */}
            <div className={`rounded-xl shadow-sm p-4 mb-6 border ${dk.card}`} data-id="element-18276" data-mp="10b5g3xwq9h0g">
              <div className="flex flex-col md:flex-row md:items-center gap-4" data-id="element-18277" data-mp="10d8ilhf2h2m2">
                <div className="relative flex-1" data-id="element-18278" data-mp="15bthndil1g8x">
                  <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${dk.textFaint}`} data-id="element-18279" data-mp="2bqkm1r6wzj7p" />

                  <input type="text" placeholder="Search authors by name, bio, or keywords..." className={`pl-10 pr-4 py-2.5 w-full rounded-lg border focus:outline-none focus:ring-2 transition-colors ${rt.inputFocus} ${dk.inputOnCard}`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-18280" data-mp="2gnudfcdv64y6" />
                </div>
                <div className={`flex rounded-lg p-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-18281" data-mp="3kjnx5m3utnj9">
                  <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? darkMode ? `bg-gray-600 ${rt.textPrimary} shadow-sm` : `bg-white ${rt.textPrimary} shadow-sm` : dk.textMuted}`} aria-label="Grid view" data-id="element-18282" data-mp="3ghkh0k68purn">
                    <LayoutGridIcon className="h-5 w-5" data-id="element-18283" data-mp="205x9kgas3xvp" />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-all ${viewMode === 'list' ? darkMode ? `bg-gray-600 ${rt.textPrimary} shadow-sm` : `bg-white ${rt.textPrimary} shadow-sm` : dk.textMuted}`} aria-label="List view" data-id="element-18284" data-mp="1v73ysvmvmkz8">
                    <LayoutListIcon className="h-5 w-5" data-id="element-18285" data-mp="5vgreu0m6q5b" />
                  </button>
                </div>
              </div>
            </div>

            {/* Results Header */}
            <div className="mb-6" data-id="element-18286" data-mp="3t0y6i2bqsm6e">
              <h2 className={`text-lg font-medium ${dk.textHeading}`} data-id="element-18287" data-mp="3t1zjiccbjiqr">
                {filteredAuthors.length} author
                {filteredAuthors.length !== 1 ? 's' : ''} found
              </h2>
            </div>

            {/* Authors Grid/List */}
            <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1 gap-4'}`} data-id="element-18288" data-mp="1ccawqmrn06nl">
              {filteredAuthors.map(author => <ProfileCard key={author.id} data={mapAuthorToProfileCard(author)} viewMode={viewMode} data-id="element-18289" data-mp="29q5gti70m2sc" />)}
            </div>

            {filteredAuthors.length === 0 && <div className={`text-center py-16 rounded-xl border-2 border-dashed ${dk.emptyState}`} data-id="element-18290" data-mp="1ztixixuc7jsf">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-18291" data-mp="2sgmzety4vhzz">
                  <UserIcon className={`h-8 w-8 ${dk.textFaint}`} data-id="element-18292" data-mp="2xd3uy97nwyn3" />
                </div>
                <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-18293" data-mp="2ika9861srm9z">
                  No authors found
                </h3>
                <p className={dk.textMuted} data-id="element-18294" data-mp="172it4ls3ge08">
                  Try adjusting your search terms or selected filters
                </p>
                <button onClick={resetFilters} className={`mt-4 font-medium text-sm ${rt.accentLink}`} data-id="element-18295" data-mp="1c3ooysh1whdz">
                  Clear all filters
                </button>
              </div>}
          </main>
        </div>
      </div>
    </div>;
};
export default AuthorDirectory;